import React, { useState } from 'react';
import _t from 'counterpart';
import UpdatePassword from '../../components/UpdatePassword';
import ConfirmationModal from '../../components/ConfirmationModal';
import { CustomerStatus, ICustomerModalsShow, SecurityLevel } from './types';
import { callCustomer, updateCustomer } from '../../services/BackendService';
import AssignableCustomers, { SalesAgentRoles } from './AssignableCustomers';
import { useMutation, useQueryClient } from 'react-query';
import { extractErrorMessage } from '../../helpers';
import EmailCustomerModal from './CustomerPage/EmailCustomerModal';
import SmsCustomerModal from './CustomerPage/SmsCustomerModal';
import WhatsAppCustomerModal from './CustomerPage/WhatsAppCustomerModal';
import AssignSalesStatusModal from './CustomerPage/AssignSalesStatusModal';
import CommissionModelChangeModal from '../commissions/CommissionModelChangeModal';
import toast from 'react-hot-toast';
import DisableCustomerModal from './DisableCustomerModal';
import MessageCustomerModal from './CustomerPage/MessageCustomerModal';
import Mt5PushCustomerModal from './CustomerPage/Mt5PushCustomerModal';
import GenerateDepositLinkModal from './CustomerPage/GenerateDepositLinkModal';

interface IProps {
  id: string;
  customer: any;
  modalsVisibility: ICustomerModalsShow;
  setTargetModalVisibility: (modalsVisiblity: ICustomerModalsShow, refetch?: boolean) => void;
  canSendSms?: boolean;
  canSendWhatsApp?: boolean;
  twilioConfigured?: boolean;
}

const CustomerModals = ({
  id,
  customer,
  modalsVisibility,
  setTargetModalVisibility,
  canSendSms = false,
  canSendWhatsApp = false,
  twilioConfigured = false,
}: IProps) => {
  const queryClient = useQueryClient();

  const hideAffiliatesModal = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, affiliatesModalShow: false }, refetch);
    document.querySelector('body')?.classList.remove('modal-open');
  };

  const hideSalesPersonModal = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, salesPersonModalShow: false }, refetch);
    document.querySelector('body')?.classList.remove('modal-open');
  };

  const updateSalesPersonMutation = useMutation(
    ({ salesPersonId, salesPersonRole }: { salesPersonId: string | null; salesPersonRole: SalesAgentRoles }) =>
      updateCustomer(id, { salesPersonId, salesAgentRole: salesPersonRole }),
    {
      onSuccess: (_, variables) => {
        if (variables.salesPersonId === null) {
          toast.success(
            variables.salesPersonRole === SalesAgentRoles.CONVERSIONAGENT
              ? _t('customer.conversion-agent-removed')
              : _t('customer.retention-agent-removed')
          );
        } else {
          toast.success(
            variables.salesPersonRole === SalesAgentRoles.CONVERSIONAGENT
              ? _t('customer.conversion-agent-assigned')
              : _t('customer.retention-agent-assigned')
          );
        }
        queryClient.invalidateQueries(['customer', id]);
        hideSalesPersonModal(true);
      },
      onError: (e) => {
        toast.error(extractErrorMessage(e));
      },
    }
  );

  const unassignSalesPerson = (salesPersonRole: SalesAgentRoles) => {
    updateSalesPersonMutation.mutate({ salesPersonId: null, salesPersonRole });
  };

  const confirmEmailMutation = useMutation(() => updateCustomer(id, { confirmEmail: true }), {
    onSuccess: () => {
      hideConfirmEmailPanel(true);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const removeRiskStatusMutation = useMutation(() => updateCustomer(id, { removeRiskStatus: true }), {
    onSuccess: () => {
      hideSetRiskStatusModal(true);
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const toggleTestFlagMutation = useMutation((isTest: boolean) => updateCustomer(id, { isTest }), {
    onSuccess: () => {
      hideToggleTestFlagModal(true);
      toast.success(_t('customer.test-flag-updated'));
    },
    onError: (e) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const changeSecurityLevelMutation = useMutation(
    (newSecurityLevel: SecurityLevel) => updateCustomer(id, { securityLevel: newSecurityLevel }),
    {
      onSuccess: () => {
        hideChangeSecurityLevelModal(true);
        toast.success(_t('customer.security-level-updated'));
      },
      onError: (e) => {
        toast.error(extractErrorMessage(e));
      },
    }
  );

  const requestUpdateCustomerStatusMutation = useMutation(
    (status: CustomerStatus) => updateCustomer(id, { customerStatus: status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('customer');
      },
      onError: (e) => {
        toast.error(extractErrorMessage(e));
      },
    }
  );

  const updateReferrerMutation = useMutation(
    (referrerId: string) => updateCustomer(id, { newReferrerId: referrerId }),
    {
      onSuccess: () => {
        hideAffiliatesModal(true);
      },
      onError: (e) => {
        toast.error(extractErrorMessage(e));
      },
    }
  );

  const hideCommissionModal = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, changeCommissionSchemeShow: false }, refetch);
  };

  const hideUpdatePassword = () => {
    setTargetModalVisibility({ ...modalsVisibility, updatePasswordShow: false });
  };

  const hideConfirmEmailPanel = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, confirmEmailShow: false }, refetch);
  };

  const hideConfirmBar = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, confirmBarShow: false }, refetch);
  };

  const closeAssignSalesStatus = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, assignSalesStatusShow: false }, refetch);
  };

  const hideEmailCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, emailCustomerModalShow: false });
  };

  const showEmailCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, emailCustomerModalShow: true });
  };

  const hideMessageCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, messageModalShow: false });
  };

  const showMessageCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, messageModalShow: true });
  };

  const hideSmsCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, smsCustomerModalShow: false });
  };

  const showSmsCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, smsCustomerModalShow: true });
  };

  const hideMt5PushCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, mt5PushCustomerModalShow: false });
  };

  const showMt5PushCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, mt5PushCustomerModalShow: true });
  };

  const hideWhatsAppCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, whatsAppCustomerModalShow: false });
  };

  const showWhatsAppCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, whatsAppCustomerModalShow: true });
  };

  const hideSetRiskStatusModal = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, removeRiskStatusShow: false }, refetch);
  };

  const hideChangeSecurityLevelModal = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, changeSecurityLevelShow: false }, refetch);
  };

  const hideToggleTestFlagModal = (refetch?: boolean) => {
    setTargetModalVisibility({ ...modalsVisibility, toggleTestFlagShow: false }, refetch);
  };

  const assignSalesPerson = (salesPersonId: string, salesPersonRole: SalesAgentRoles | undefined) => {
    if (salesPersonRole) {
      updateSalesPersonMutation.mutate({ salesPersonId, salesPersonRole });
    }
  };

  const removeRiskStatus = () => {
    removeRiskStatusMutation.mutate();
  };

  const changeSecurityLevel = () => {
    const newSecurityLevel =
      customer.securityLevel === SecurityLevel.Sensitive ? SecurityLevel.Regular : SecurityLevel.Sensitive;
    changeSecurityLevelMutation.mutate(newSecurityLevel);
  };

  const toggleTestFlag = () => {
    toggleTestFlagMutation.mutate(!customer.isTest);
  };

  const confirmEmail = () => {
    confirmEmailMutation.mutate();
  };

  const requestChangeCustomerStatus = (status: CustomerStatus) => {
    if (status === CustomerStatus.Disabled) {
      requestUpdateCustomerStatusMutation.mutate(CustomerStatus.Disabled);
    } else {
      requestUpdateCustomerStatusMutation.mutate(CustomerStatus.Active);
    }
  };

  const assignReferrer = (referrerId: string) => {
    updateReferrerMutation.mutate(referrerId);
  };

  const { status, mobilenumber, terminationRequestedAt } = customer;

  const hideCallCustomerModal = () => {
    setTargetModalVisibility({ ...modalsVisibility, callCustomerModalShow: false }, false);
  };

  const callCustomerMutation = useMutation((id: string) => callCustomer(id), {
    onSuccess: () => {
      hideCallCustomerModal();
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error));
    },
  });

  const invokeCallMutation = () => {
    callCustomerMutation.mutate(id);
  };

  return (
    <>
      {modalsVisibility.changeCommissionSchemeShow && (
        <CommissionModelChangeModal userId={id!} hideModal={hideCommissionModal} />
      )}
      <ConfirmationModal
        id={id}
        show={modalsVisibility.callCustomerModalShow}
        onConfirm={invokeCallMutation}
        hidePanel={hideCallCustomerModal}
        title={_t('customer.call-customer-on', { number: mobilenumber })}
        error={null}
        buttonColor="primary"
        withMutation
      />

      <DisableCustomerModal
        id={id}
        show={['ACTIVE', 'PENDING'].includes(status) && modalsVisibility.confirmBarShow}
        onConfirm={requestChangeCustomerStatus}
        hidePanel={hideConfirmBar}
        title={_t('customer.disable-user')}
        error={null}
        buttonColor="danger"
        terminationRequested={terminationRequestedAt ? true : false}
      />

      <ConfirmationModal
        id={id}
        show={status === 'DISABLED' && modalsVisibility.confirmBarShow}
        onConfirm={requestChangeCustomerStatus}
        hidePanel={hideConfirmBar}
        title={_t('customer.enable-user')}
        error={null}
        buttonColor="primary"
        withMutation
      />

      <ConfirmationModal
        id={id}
        show={modalsVisibility.removeRiskStatusShow}
        onConfirm={removeRiskStatus}
        hidePanel={hideSetRiskStatusModal}
        title={_t('customer.remove-risk-status')}
        error={null}
        buttonColor="primary"
        withMutation
      />

      {modalsVisibility.salesPersonModalShow && (
        <AssignableCustomers
          title={_t('customer.agents-list')}
          show={modalsVisibility.salesPersonModalShow}
          onClose={hideSalesPersonModal}
          queryKey="sales-people"
          action={assignSalesPerson}
          unassignAction={unassignSalesPerson}
          roles={['SALES']}
          assignSalesAgent
          currentConversionAgent={customer?.conversionAgent}
          currentRetentionAgent={customer?.retentionAgent}
        />
      )}

      {modalsVisibility.affiliatesModalShow && (
        <AssignableCustomers
          title={_t('customer.customers-list')}
          show={modalsVisibility.affiliatesModalShow}
          onClose={hideAffiliatesModal}
          queryKey="affiliates-assign"
          action={assignReferrer}
          roles={null}
          assignSalesAgent={false}
        />
      )}

      <UpdatePassword
        show={modalsVisibility.updatePasswordShow}
        customerId={customer.id}
        onClose={hideUpdatePassword}
      />

      <ConfirmationModal
        id={id}
        show={modalsVisibility.confirmEmailShow}
        hidePanel={hideConfirmEmailPanel}
        onConfirm={confirmEmail}
        title={_t('customer.confirm-email-question')}
        error={null}
        buttonColor="primary"
        withMutation
      />

      <ConfirmationModal
        id={id}
        show={modalsVisibility.changeSecurityLevelShow}
        hidePanel={hideChangeSecurityLevelModal}
        onConfirm={changeSecurityLevel}
        title={
          customer.securityLevel === SecurityLevel.Sensitive
            ? _t('customer.set-security-normal-question')
            : _t('customer.set-security-sensitive-question')
        }
        description={
          customer.securityLevel === SecurityLevel.Sensitive
            ? _t('customer.set-security-normal-description')
            : _t('customer.set-security-sensitive-description')
        }
        error={null}
        buttonColor="primary"
        withMutation
      />

      <ConfirmationModal
        id={id}
        show={modalsVisibility.toggleTestFlagShow}
        hidePanel={hideToggleTestFlagModal}
        onConfirm={toggleTestFlag}
        title={customer.isTest ? _t('customer.unmark-as-test-question') : _t('customer.mark-as-test-question')}
        description={
          customer.isTest ? _t('customer.unmark-as-test-description') : _t('customer.mark-as-test-description')
        }
        error={null}
        buttonColor="primary"
        withMutation
      />

      <AssignSalesStatusModal
        show={modalsVisibility.assignSalesStatusShow}
        onClose={closeAssignSalesStatus}
        customerId={customer.id}
      />

      <EmailCustomerModal
        show={modalsVisibility.emailCustomerModalShow}
        onClose={hideEmailCustomerModal}
        customer={customer!}
        onCloseDiscardChanges={showEmailCustomerModal}
      />

      <MessageCustomerModal
        show={modalsVisibility.messageModalShow}
        onClose={hideMessageCustomerModal}
        customer={customer!}
        onCloseDiscardChanges={showMessageCustomerModal}
      />

      <SmsCustomerModal
        show={modalsVisibility.smsCustomerModalShow}
        onClose={hideSmsCustomerModal}
        customer={customer!}
        onCloseDiscardChanges={showSmsCustomerModal}
      />

      <Mt5PushCustomerModal
        show={modalsVisibility.mt5PushCustomerModalShow}
        onClose={hideMt5PushCustomerModal}
        customer={customer!}
        onCloseDiscardChanges={showMt5PushCustomerModal}
      />

      <WhatsAppCustomerModal
        show={modalsVisibility.whatsAppCustomerModalShow}
        onClose={hideWhatsAppCustomerModal}
        customer={customer!}
        onCloseDiscardChanges={showWhatsAppCustomerModal}
      />

      <GenerateDepositLinkModal
        show={modalsVisibility.depositLinkModalShow}
        onClose={() => setTargetModalVisibility({ ...modalsVisibility, depositLinkModalShow: false })}
        customerId={customer.id}
        customerName={customer.name}
        canSendSms={canSendSms}
        canSendWhatsApp={canSendWhatsApp}
        twilioConfigured={twilioConfigured}
        customerHasPhone={Boolean(customer.mobilenumber)}
      />
    </>
  );
};

export default CustomerModals;
